import {
  SearchableSelect,
  SearchableSelectOption,
} from '@spotted-zebra-uk/ui-components';
import { SupportLink } from 'components/atoms/SupportLink/SupportLink';
import { PRIVACY_POLICY_URL } from 'constants/privacyPolicyLink';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './AutomatedDecisionMaking.module.scss';

const PrivacyPolicyLink = () => {
  const { t } = useTranslation();
  return (
    <a
      className={styles.link}
      href={PRIVACY_POLICY_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t('automatedDecisionMaking.privacyPolicy')}
    </a>
  );
};

/**
 * The first two paragraphs are identical across every region, so they are
 * shared rather than duplicated per region.
 */
const SharedIntro = () => {
  const { t } = useTranslation();
  return (
    <>
      <p className={styles.paragraph}>
        {t('automatedDecisionMaking.sharedIntro1')}
      </p>
      <p className={styles.paragraph}>
        {t('automatedDecisionMaking.sharedIntro2')}
      </p>
    </>
  );
};

/** Shared "contact us / privacy policy" closing paragraph. */
const ContactParagraph = ({ i18nKey }: { i18nKey: string }) => (
  <p className={styles.paragraph}>
    <Trans
      i18nKey={i18nKey}
      components={{ 1: <SupportLink />, 2: <PrivacyPolicyLink /> }}
    />
  </p>
);

const REGION_IDS = [
  'united-kingdom',
  'european-union',
  'brazil',
  'canada',
  'colombia',
  'india',
  'mexico',
  'pakistan',
  'poland',
  'united-states',
] as const;

type RegionId = (typeof REGION_IDS)[number];

const REGION_LABEL_KEYS: Record<RegionId, string> = {
  'united-kingdom': 'automatedDecisionMaking.regions.unitedKingdom',
  'european-union': 'automatedDecisionMaking.regions.europeanUnion',
  brazil: 'automatedDecisionMaking.regions.brazil',
  canada: 'automatedDecisionMaking.regions.canada',
  colombia: 'automatedDecisionMaking.regions.colombia',
  india: 'automatedDecisionMaking.regions.india',
  mexico: 'automatedDecisionMaking.regions.mexico',
  pakistan: 'automatedDecisionMaking.regions.pakistan',
  poland: 'automatedDecisionMaking.regions.poland',
  'united-states': 'automatedDecisionMaking.regions.unitedStates',
};

const STORAGE_KEY = 'adm-selected-region';

function readStoredRegion(): RegionId | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return REGION_IDS.includes(stored as RegionId) ? (stored as RegionId) : null;
  } catch {
    return null;
  }
}

function storeRegion(regionId: RegionId | null) {
  try {
    if (regionId) {
      window.localStorage.setItem(STORAGE_KEY, regionId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (e.g. private browsing) — selection won't persist.
  }
}

export function AutomatedDecisionMaking() {
  const { t, i18n } = useTranslation();
  const [activeRegionId, setActiveRegionId] = useState<RegionId | null>(
    readStoredRegion
  );

  // United Kingdom pinned first, remaining regions alphabetical by localised
  // label. SearchableSelect moves the selected option to the top itself, so a
  // selected region appears first with United Kingdom directly below it.
  const regionOptions = useMemo<SearchableSelectOption<RegionId>[]>(() => {
    const rest = REGION_IDS.filter(regionId => regionId !== 'united-kingdom')
      .map(regionId => ({
        value: regionId,
        label: t(REGION_LABEL_KEYS[regionId]),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    return [
      {
        value: 'united-kingdom' as const,
        label: t(REGION_LABEL_KEYS['united-kingdom']),
      },
      ...rest,
    ];
  }, [t, i18n.language]);

  const handleRegionChange = (next: RegionId | undefined) => {
    const nextRegion = next ?? null;
    setActiveRegionId(nextRegion);
    storeRegion(nextRegion);
  };

  function renderRegionContent(regionId: RegionId) {
    switch (regionId) {
      case 'united-kingdom':
        return (
          <>
            <SharedIntro />
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.legitimateInterest')}
            </p>
            <p className={styles.rightsHeading}>
              {t('automatedDecisionMaking.rightsHeading')}
            </p>
            <ul className={styles.rightsList}>
              <li>{t('automatedDecisionMaking.rights.reviewByPerson')}</li>
              <li>{t('automatedDecisionMaking.rights.sharePointOfView')}</li>
              <li>{t('automatedDecisionMaking.rights.contestDecision')}</li>
            </ul>
            <ContactParagraph i18nKey="automatedDecisionMaking.contactAny" />
          </>
        );
      // Poland currently has the same rights as the rest of the EU.
      case 'european-union':
      case 'poland':
        return (
          <>
            <SharedIntro />
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.euConsent')}
            </p>
            <p className={styles.rightsHeading}>
              {t('automatedDecisionMaking.rightsHeading')}
            </p>
            <ul className={styles.rightsList}>
              <li>{t('automatedDecisionMaking.rights.reviewByPerson')}</li>
              <li>{t('automatedDecisionMaking.rights.sharePointOfView')}</li>
              <li>{t('automatedDecisionMaking.rights.contestDecision')}</li>
              <li>
                {t('automatedDecisionMaking.rights.meaningfulInformation')}
              </li>
            </ul>
            <ContactParagraph i18nKey="automatedDecisionMaking.contactAny" />
          </>
        );
      case 'brazil':
        return (
          <>
            <SharedIntro />
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.legitimateInterest')}
            </p>
            <p className={styles.rightsHeading}>
              {t('automatedDecisionMaking.rightsHeading')}
            </p>
            <ul className={styles.rightsList}>
              <li>{t('automatedDecisionMaking.rights.requestReview')}</li>
              <li>{t('automatedDecisionMaking.rights.requestInformation')}</li>
            </ul>
            <ContactParagraph i18nKey="automatedDecisionMaking.contactEither" />
          </>
        );
      case 'canada':
        return (
          <>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.sharedIntro1')}
            </p>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.canadaOversight')}
            </p>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.euConsent')}
            </p>
            <p className={styles.rightsHeading}>
              {t('automatedDecisionMaking.rightsHeading')}
            </p>
            <ul className={styles.rightsList}>
              <li>
                {t('automatedDecisionMaking.rights.reviewAndSharePointOfView')}
              </li>
              <li>
                {t('automatedDecisionMaking.rights.requestDecisionInformation')}
              </li>
              <li>{t('automatedDecisionMaking.rights.correctInformation')}</li>
            </ul>
            <ContactParagraph i18nKey="automatedDecisionMaking.contactAny" />
          </>
        );
      case 'india':
        return (
          <>
            <SharedIntro />
            <p className={styles.paragraph}>
              <Trans
                i18nKey="automatedDecisionMaking.india"
                components={{ 1: <SupportLink />, 2: <PrivacyPolicyLink /> }}
              />
            </p>
          </>
        );
      case 'colombia':
        return (
          <>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.sharedIntro1')}
            </p>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.sharedIntro2')}
            </p>
            <ContactParagraph i18nKey="automatedDecisionMaking.privacyDetailContact" />
          </>
        );
      case 'pakistan':
        return (
          <>
            <SharedIntro />
            <p className={styles.paragraph}>
              <Trans
                i18nKey="automatedDecisionMaking.pakistanConsent"
                components={{ 2: <PrivacyPolicyLink /> }}
              />
            </p>
            <ContactParagraph i18nKey="automatedDecisionMaking.contactQuestions" />
          </>
        );
      case 'united-states':
        return (
          <>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.usIntro')}
            </p>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.sharedIntro2')}
            </p>
            <p className={styles.rightsHeading}>
              {t('automatedDecisionMaking.usRightsHeading')}
            </p>
            <ul className={styles.rightsList}>
              <li>{t('automatedDecisionMaking.rights.alternativeProcess')}</li>
              <li>{t('automatedDecisionMaking.rights.reviewByPerson')}</li>
            </ul>
            <ContactParagraph i18nKey="automatedDecisionMaking.usPrivacyContact" />
          </>
        );
      case 'mexico':
        return (
          <>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.sharedIntro1')}
            </p>
            <p className={styles.rightsHeading}>
              <Trans
                i18nKey="automatedDecisionMaking.privacyDetailRightsHeading"
                components={{ 2: <PrivacyPolicyLink /> }}
              />
            </p>
            <ul className={styles.rightsList}>
              <li>{t('automatedDecisionMaking.rights.reviewByPerson')}</li>
              <li>{t('automatedDecisionMaking.rights.sharePointOfView')}</li>
              <li>{t('automatedDecisionMaking.rights.contestDecision')}</li>
              <li>{t('automatedDecisionMaking.rights.requestInformation')}</li>
            </ul>
            <p className={styles.paragraph}>
              {t('automatedDecisionMaking.sharedIntro2')}
            </p>
            <ContactParagraph i18nKey="automatedDecisionMaking.contactExercise" />
          </>
        );
    }
  }

  return (
    <div className={styles.page}>
      <article className={styles.article}>
        <h1 className={styles.title}>{t('automatedDecisionMaking.title')}</h1>
        <p className={styles.intro}>{t('automatedDecisionMaking.intro')}</p>
        <p className={styles.intro}>
          {t('automatedDecisionMaking.introSelect')}
        </p>
        <p className={styles.intro}>
          <Trans
            i18nKey="automatedDecisionMaking.introContact"
            components={{ 1: <SupportLink className={styles.introLink} /> }}
          />
        </p>

        <div className={styles.regionSelect}>
          {/* Uncontrolled: passing value=undefined for "no selection" would
              flip the component into uncontrolled mode anyway, resurrecting
              its stale internal selection after a clear. */}
          <SearchableSelect<RegionId>
            options={regionOptions}
            defaultValue={activeRegionId ?? undefined}
            onValueChange={handleRegionChange}
            label={t('automatedDecisionMaking.regionLabel')}
            alwaysShowLabel
            searchPlaceholder=""
            noMatchesMessage={t('automatedDecisionMaking.noMatches')}
            aria-label={t('automatedDecisionMaking.selectRegion')}
          />
        </div>

        <section aria-live="polite">
          {activeRegionId ? (
            <>
              <hr className={styles.divider} />
              <h2 className={styles.regionTitle}>
                {t(REGION_LABEL_KEYS[activeRegionId])}
              </h2>
              {renderRegionContent(activeRegionId)}
            </>
          ) : (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateTitle}>
                {t('automatedDecisionMaking.noRegionTitle')}
              </p>
              <p className={styles.emptyStateBody}>
                {t('automatedDecisionMaking.noRegionBody')}
              </p>
            </div>
          )}
        </section>
      </article>
    </div>
  );
}
